import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useAuth } from '@/hooks/useAuth';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Store, Mail, Lock, MapPin, Phone, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { VENDOR_CATEGORIES } from '@/types/vendor';
import { registerSchema } from '@/lib/auth-schema';
import { PasswordStrength } from '@/components/auth/password-strength';
import {
  ensureVendorProvisioned,
  generateVendorSlug,
  type VendorSignupData,
} from '@/lib/vendor-provisioning';

// Cloudflare's published always-pass dev sitekey. Real production sitekey is
// set via VITE_TURNSTILE_SITE_KEY by infra. Same wiring as Auth.tsx (pareja).
const TURNSTILE_DEV_SITE_KEY = '1x00000000000000000000AA';
const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? TURNSTILE_DEV_SITE_KEY;

const VendorAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, signIn, signUp, resendConfirmation, signOut } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    category: 'venue',
    location: '',
    phone: '',
    description: '',
  });

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  // Email-confirmation pending panel — compartido por alta y login-sin-confirmar.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);

  const resetTurnstile = () => {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  };

  // Aprovisionamiento diferido (007 · TV-1): al aterrizar en /vendor-auth con
  // sesión activa (p. ej. tras confirmar el email), completa rol + perfil y
  // entra al backoffice. Para una cuenta que no es de proveedor, no hace nada.
  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      try {
        const result = await ensureVendorProvisioned();
        if (!active) return;
        if (result === 'provisioned' || result === 'already') {
          queryClient.invalidateQueries({ queryKey: ['is-vendor'] });
          queryClient.invalidateQueries({ queryKey: ['vendor-profile'] });
          navigate('/backoffice');
        }
      } catch {
        // Se queda en la página; puede reintentar iniciando sesión.
      }
    })();
    return () => {
      active = false;
    };
  }, [user, navigate, queryClient]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaToken) {
      toast.error('Completa la verificación para continuar');
      return;
    }
    setIsLoading(true);

    const { error } = await signIn(loginEmail, loginPassword, captchaToken);

    if (error) {
      resetTurnstile();
      const code = (error as { code?: string }).code;
      const looksUnconfirmed =
        code === 'email_not_confirmed' ||
        /email.*not.*confirmed/i.test(error.message);

      if (looksUnconfirmed) {
        setPendingEmail(loginEmail);
      } else {
        toast.error('Error al iniciar sesión: ' + error.message);
      }
      setIsLoading(false);
      return;
    }

    try {
      const result = await ensureVendorProvisioned();
      if (result === 'provisioned' || result === 'already') {
        queryClient.invalidateQueries({ queryKey: ['is-vendor'] });
        queryClient.invalidateQueries({ queryKey: ['vendor-profile'] });
        toast.success('¡Bienvenido!');
        navigate('/backoffice');
      } else {
        // Autenticado pero sin rol vendor ni alta pendiente → no es proveedor.
        await signOut();
        toast.error('Esta cuenta no está registrada como proveedor');
      }
    } catch {
      toast.error('No pudimos completar tu registro de proveedor. Intenta de nuevo.');
    }

    setIsLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsed = registerSchema.safeParse({
      email: registerData.email,
      password: registerData.password,
      confirmPassword: registerData.confirmPassword,
    });

    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Datos inválidos');
      return;
    }

    if (!registerData.businessName.trim()) {
      toast.error('El nombre del negocio es obligatorio');
      return;
    }

    if (!captchaToken) {
      toast.error('Completa la verificación para continuar');
      return;
    }

    setIsLoading(true);

    // Aprovisionamiento diferido (007 · TV-1): NO se llama grant_vendor_role aquí
    // (no hay sesión si la confirmación de email está activada). Los datos del
    // negocio viajan en user_metadata y se aprovisionan en el primer login.
    const vendorSignup: VendorSignupData = {
      name: registerData.businessName.trim(),
      slug: generateVendorSlug(registerData.businessName),
      category: registerData.category,
      location: registerData.location.trim() || null,
      contact_phone: registerData.phone.trim() || null,
      contact_email: registerData.email,
      description: registerData.description.trim() || null,
    };

    const { error } = await signUp(registerData.email, registerData.password, {
      captchaToken,
      emailRedirectTo: `${window.location.origin}/vendor-auth`,
      data: { vendor_signup: vendorSignup },
    });

    if (error) {
      toast.error('Error al registrarse: ' + error.message);
      resetTurnstile();
    } else {
      setPendingEmail(registerData.email);
    }

    setIsLoading(false);
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    if (!captchaToken) {
      toast.error('Completa la verificación para reenviar');
      return;
    }
    setResendLoading(true);
    const { error } = await resendConfirmation(pendingEmail, captchaToken);
    resetTurnstile();
    if (error) {
      toast.error('No pudimos reenviar el correo: ' + error.message);
    } else {
      toast.success('Correo reenviado. Revisa tu bandeja de entrada.');
    }
    setResendLoading(false);
  };

  if (pendingEmail) {
    return (
      <Layout>
        <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-secondary/10">
                <MailCheck className="h-6 w-6 text-secondary" />
              </div>
              <CardTitle className="text-2xl font-display">Confirma tu correo</CardTitle>
              <CardDescription>
                Te enviamos un correo a <strong>{pendingEmail}</strong>. Confirma para
                acceder a tu portal de proveedor.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground text-center">
                Puede tardar un minuto en llegar. Si no lo ves, revisa spam o reenvíalo.
              </p>
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                  options={{ theme: 'auto' }}
                />
              </div>
              <Button onClick={handleResend} className="w-full" disabled={resendLoading || !captchaToken}>
                {resendLoading ? 'Enviando...' : 'Reenviar correo'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setPendingEmail(null)}
              >
                Volver
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-secondary/10">
              <Store className="h-6 w-6 text-secondary" />
            </div>
            <CardTitle className="text-2xl font-display">Portal de Proveedores</CardTitle>
            <CardDescription>
              Gestiona tu perfil y conecta con parejas que buscan tus servicios
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Iniciar Sesión</TabsTrigger>
                <TabsTrigger value="register">Registrarse</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="space-y-4 mt-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="vendor-login-email">Correo electrónico</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="vendor-login-email"
                        type="email"
                        placeholder="tu@negocio.com"
                        className="pl-10"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vendor-login-password">Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="vendor-login-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                      onError={() => setCaptchaToken(null)}
                      options={{ theme: 'auto' }}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading || !captchaToken}>
                    {isLoading ? 'Cargando...' : 'Iniciar Sesión'}
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    className="w-full text-muted-foreground"
                    onClick={() => navigate('/reset-password')}
                  >
                    ¿Olvidaste tu contraseña?
                  </Button>
                </form>
                <div className="text-center text-sm text-muted-foreground">
                  <Link to="/auth" className="hover:text-primary underline">
                    ¿Eres una pareja? Accede aquí
                  </Link>
                </div>
              </TabsContent>

              <TabsContent value="register" className="space-y-4 mt-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="business-name">Nombre del Negocio *</Label>
                      <div className="relative">
                        <Store className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="business-name"
                          type="text"
                          placeholder="Finca El Olivar"
                          className="pl-10"
                          value={registerData.businessName}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, businessName: e.target.value }))}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vendor-category">Categoría *</Label>
                      <Select
                        value={registerData.category}
                        onValueChange={(value) => setRegisterData(prev => ({ ...prev, category: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          {VENDOR_CATEGORIES.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.icon} {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vendor-location">Ubicación</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="vendor-location"
                          type="text"
                          placeholder="Madrid, España"
                          className="pl-10"
                          value={registerData.location}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, location: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vendor-email">Correo electrónico *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="vendor-email"
                          type="email"
                          placeholder="tu@negocio.com"
                          className="pl-10"
                          value={registerData.email}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vendor-phone">Teléfono</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="vendor-phone"
                          type="tel"
                          placeholder="+34 600 000 000"
                          className="pl-10"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, phone: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="vendor-description">Descripción breve</Label>
                      <Textarea
                        id="vendor-description"
                        placeholder="Describe tu negocio en pocas palabras..."
                        value={registerData.description}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vendor-password">Contraseña *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="vendor-password"
                          type="password"
                          placeholder="Mínimo 10, con mayúscula y número"
                          className="pl-10"
                          value={registerData.password}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                          required
                          minLength={10}
                        />
                      </div>
                      <PasswordStrength password={registerData.password} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vendor-confirm">Confirmar contraseña *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="vendor-confirm"
                          type="password"
                          placeholder="Repite tu contraseña"
                          className="pl-10"
                          value={registerData.confirmPassword}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                      onError={() => setCaptchaToken(null)}
                      options={{ theme: 'auto' }}
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Tu perfil será revisado por nuestro equipo antes de aparecer en el directorio.
                  </p>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading || !captchaToken}
                  >
                    {isLoading ? 'Cargando...' : 'Crear Cuenta de Proveedor'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default VendorAuth;
